import { useRef } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiGlobe,
  FiCalendar,
  FiHash,
} from 'react-icons/fi';
import { FaBarcode, FaBuilding } from 'react-icons/fa';

const PLACEHOLDER_ISBN10 = '0000000000';
const PLACEHOLDER_ISBN13 = '9780000000000';
const DASH = '—';

const formatDate = (raw) => {
  if (!raw) return DASH;
  return new Date(raw).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Books store dimensions in mm; show cm to match the catalogue convention.
const toCm = (mm) => {
  const n = Number(mm);
  return Number.isFinite(n) && n > 0 ? (n / 10).toFixed(2) : null;
};

const Item = ({ label, icon: Icon, children }) => (
  <li className="snap-start shrink-0 w-40 px-4 text-center border-r border-gray-200 last:border-r-0">
    <p className="text-xs text-black mb-2">{label}</p>
    <Icon className="mx-auto text-black text-2xl mb-2" />
    <p className="text-sm text-black font-semibold leading-tight">{children}</p>
  </li>
);

const AttributesSlider = ({ book }) => {
  const stripRef = useRef(null);

  const scroll = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    const delta = el.clientWidth * 0.8 * (dir === 'next' ? 1 : -1);
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const w = toCm(book.width);
  const s = toCm(book.spineWidth);
  const h = toCm(book.height);
  const dimensions = w && h ? `${w} x ${s || '-'} x ${h} cm` : DASH;

  const isbn10 =
    book.isbn10 && book.isbn10 !== PLACEHOLDER_ISBN10 ? book.isbn10 : DASH;
  const isbn13 =
    book.isbn13 && book.isbn13 !== PLACEHOLDER_ISBN13 ? book.isbn13 : DASH;

  return (
    <section className="mt-6 relative border-y border-gray-200 py-5">
      <button
        type="button"
        aria-label="Previous"
        onClick={() => scroll('prev')}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-gray-300 shadow items-center justify-center text-black hover:bg-gray-50"
      >
        <FiChevronLeft />
      </button>

      <ul
        ref={stripRef}
        className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory list-none m-0 p-0 mx-0 md:mx-10 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <Item label="ISBN-10" icon={FaBarcode}>{isbn10}</Item>
        <Item label="ISBN-13" icon={FaBarcode}>{isbn13}</Item>
        <Item label="Edition" icon={FiHash}>{book.edition || DASH}</Item>
        <Item label="Binding" icon={FiFileText}>{book.binding || DASH}</Item>
        <Item label="Publisher" icon={FaBuilding}>{book.publisher || DASH}</Item>
        <Item label="Publication date" icon={FiCalendar}>
          {formatDate(book.publisherDate)}
        </Item>
        <Item label="Language" icon={FiGlobe}>{book.language || 'English'}</Item>
        <Item label="Dimensions" icon={FiFileText}>{dimensions}</Item>
        <Item label="Print length" icon={FiFileText}>
          {book.pages > 0 ? `${book.pages} pages` : DASH}
        </Item>
      </ul>

      <button
        type="button"
        aria-label="Next"
        onClick={() => scroll('next')}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-gray-300 shadow items-center justify-center text-black hover:bg-gray-50"
      >
        <FiChevronRight />
      </button>
    </section>
  );
};

export default AttributesSlider;
